import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import './LegalPage.css';

const WhatsappIcon = FaWhatsapp as unknown as React.FC<{
  'aria-hidden'?: boolean;
  style?: React.CSSProperties;
}>;

const whatsappLink = (
  <a href="https://wa.me/919220891200" target="_blank" rel="noopener noreferrer">
    <WhatsappIcon aria-hidden style={{ color: '#25D366', verticalAlign: '-0.15em' }} /> +91
    92208 91200
  </a>
);

const tableOfContents = [
  'Who We Are',
  'Plain-Language Summary',
  'Information We Collect',
  'Google and YouTube API Services',
  'How We Use Your Information',
  'AI and Automated Processing',
  'How We Share Your Information',
  'Subprocessors',
  'Data Retention',
  'Managing and Revoking Connected Accounts',
  'Your Rights and Choices',
  'Deleting Your Account and Data',
  'Cookies',
  'Data Security',
  'International Data Transfers',
  "Children's Privacy",
  'Changes to This Policy',
  'Contact Us',
];

const Privacy: React.FC = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Privacy Policy — ViralAI</title>
        <meta name="description" content="ViralAI privacy policy — exactly what data we collect, which YouTube and Google scopes we request and why, how long we keep it, and how to delete it." />
        <link rel="canonical" href="https://www.vimapi.com/privacy" />
        <meta property="og:url" content="https://www.vimapi.com/privacy" />
        <meta property="og:title" content="Privacy Policy — ViralAI" />
        <meta property="og:description" content="ViralAI privacy policy — exactly what data we collect, which YouTube and Google scopes we request and why, how long we keep it, and how to delete it." />
      </Helmet>
      <nav className="legal-nav">
        <Link to="/" className="legal-brand" aria-label="ViralAI home">
          <img src="/viralai-logo.svg" alt="VimAPI — A ViralAI Platform" className="legal-logo" />
        </Link>
        <Link to="/" className="back-link">← Back to Home</Link>
      </nav>

      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="effective-date">
          <strong>Effective Date:</strong> September 9, 2026 &nbsp;·&nbsp; <strong>Last Updated:</strong> September 9, 2026
        </p>

        <p>
          ViralAI.Tech ("we," "us," and/or "our") is dedicated to protecting your personal
          information and informing you about how we use it. This Privacy Policy (the "Privacy
          Policy" or the "Policy") is intended to provide information to visitors to our websites
          and users of our applications ("you" or "your") about the personal information we collect,
          and how we source, use, disclose, and protect your information when you access or use (1)
          our websites located at <a href="https://www.vimapi.com">https://www.vimapi.com</a> and{' '}
          <a href="https://www.viralai.tech">https://www.viralai.tech</a>, and all of our other
          websites to which this Privacy Policy is posted (collectively, the "Website"); (2) our
          application(s) and APIs to which this Privacy Policy is posted (collectively, the
          "Application"); and (3) any services, content, communications, and product features
          relating to the Website and Application (together with the Website and the Application,
          the "Service"). This Privacy Policy covers the privacy practices of ViralAI.Tech only. It
          does not apply to the practices of third-party websites, services, or applications,
          including third parties with which we partner and the platforms you connect (such as
          YouTube, Meta, X, and LinkedIn). These third-party services are governed by each such
          third party's own privacy policy. ViralAI.Tech is not responsible for the privacy
          policies and practices of websites other than its own, or for the failure of unaffiliated
          third parties to honor their privacy obligations. This Privacy Policy is organized as
          follows:
        </p>

        <p><strong>Table of Contents</strong></p>
        <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
          {tableOfContents.map((title, i) => (
            <li key={title}>
              {i + 1}. {title}
            </li>
          ))}
        </ul>

        <section>
          <h2>1. Who We Are</h2>
          <p>
            ViralAI is an AI service that turns long-form videos into short vertical clips and
            helps you publish them to your social channels. ViralAI.Tech ("the operator") operates
            ViralAI and VimAPI. This Privacy Policy explains what information we collect through
            the Service, why we collect it, who we share it with, how long we keep it, and the
            choices you have.
          </p>
          <p>
            This is not a generic template. Every category below reflects what the ViralAI product
            actually does. If something here is unclear, email us at{' '}
            <a href="mailto:support@vimapi.com">support@vimapi.com</a> or{' '}
            <a href="mailto:creator@viralai.tech">creator@viralai.tech</a> before you use the
            Service.
          </p>
        </section>

        <section>
          <h2>2. Plain-Language Summary</h2>
          <ul>
            <li>
              <strong>What we collect:</strong> your account details, the videos and clips you
              upload or generate, tokens for the social accounts you connect, payment records, and
              basic device/usage data.
            </li>
            <li>
              <strong>Google / YouTube:</strong> signing in uses only your name, email, and profile
              picture. Publishing scopes (upload, read, analytics) are requested separately, only
              when you connect a YouTube channel, and only to do what you ask.
            </li>
            <li>
              <strong>We do not sell your data</strong> and we do not use your content or your
              Google/YouTube data to train AI models.
            </li>
            <li>
              <strong>Deletion:</strong> you can delete any upload, any clip, or your whole account
              at any time. Account deletion removes your content from active systems within 30 days
              and from backups within 90 days.
            </li>
            <li>
              <strong>Subprocessors we use:</strong> Amazon Web Services (storage and compute),
              OpenAI (transcription fallback and text suggestions), Razorpay (payments), and a
              transactional email provider. Full list in Section 8.
            </li>
          </ul>
          <p>
            The sections below are the authoritative version; this summary is for convenience only.
          </p>
        </section>

        <section>
          <h2>3. Information We Collect</h2>

          <h3>3.1 Information you provide</h3>
          <ul>
            <li>
              <strong>Account information:</strong> your name, email address, and profile picture.
              When you sign up with Google, we receive these from your Google profile; we do not
              receive or store your Google password. We separately ask you to provide your phone
              number, pincode, and address for billing and invoices; these do not come from Google.
            </li>
            <li>
              <strong>Waitlist information:</strong> if you join our waitlist, we store the email
              address you submit until you either create an account or ask us to remove it.
            </li>
            <li>
              <strong>Content you upload or create:</strong> source video and audio files,
              generated short clips, transcripts and captions, thumbnails, titles, descriptions,
              tags, scheduling details, and drafts.
            </li>
            <li>
              <strong>Payment information:</strong> when you buy credits or a plan, our payment
              processor (Razorpay) collects and processes your card or UPI details. We receive
              transaction identifiers, the amount, status, and partial card metadata — <em>not</em>{' '}
              your full card number.
            </li>
            <li>
              <strong>Support communications:</strong> messages, attachments, and contact details
              you send us by email or WhatsApp.
            </li>
          </ul>

          <h3>3.2 Information from connected accounts</h3>
          <p>
            You can connect third-party accounts so ViralAI can publish and measure content on your
            behalf. When you do, we store an encrypted access token (and refresh token where
            provided) plus the profile and channel data listed below. You can disconnect any account
            at any time (Section 10).
          </p>
          <ul>
            <li>
              <strong>Google / YouTube:</strong> channel ID and handle, channel title and
              thumbnail, the email on the account, video metadata for videos you publish through
              ViralAI, upload and processing status, and channel/video analytics (views, watch time,
              subscriber changes, traffic sources, and audience geography). See Section 4 for the
              exact scopes.
            </li>
            <li>
              <strong>Meta, X, and LinkedIn:</strong> we store only the access token needed to
              publish on your behalf at your request.
            </li>
          </ul>

          <h3>3.3 Information we collect automatically</h3>
          <ul>
            <li>
              <strong>Device and connection data:</strong> IP address, browser type, operating
              system, and device identifiers.
            </li>
            <li>
              <strong>Usage data:</strong> pages and features used, actions taken (uploads, edits,
              publishes), timestamps, and error/diagnostic logs.
            </li>
            <li>
              <strong>Cookies:</strong> a session cookie to keep you signed in and a small number of
              preference cookies. We do <strong>not</strong> use third-party advertising or
              cross-site tracking cookies. See Section 12.
            </li>
          </ul>
        </section>

        <section>
          <h2>4. Google and YouTube API Services</h2>
          <p>
            ViralAI uses YouTube API Services and other Google APIs. Your use of features that rely
            on them is also subject to the{' '}
            <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
              YouTube Terms of Service
            </a>{' '}
            and the{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Google Privacy Policy
            </a>.
          </p>

          <h3>4.1 Scopes we request, and why</h3>
          <p>
            <strong>Signing in</strong> requests only <code>openid</code>, <code>profile</code>, and{' '}
            <code>email</code> — enough to create your account and show your name and picture. It
            grants no access to your videos or channel.
          </p>
          <p>
            <strong>Connecting a YouTube channel</strong> is a separate step with its own consent
            screen. Only then do we request:
          </p>
          <ul>
            <li>
              <code>https://www.googleapis.com/auth/youtube.upload</code> — to publish the short
              clips you generate to your channel, with the title, description, thumbnail, tags, and
              visibility you set, when you choose to publish them.
            </li>
            <li>
              <code>https://www.googleapis.com/auth/youtube.readonly</code> — to list your channels
              and existing videos so you can pick where content goes and see your library inside
              ViralAI.
            </li>
            <li>
              <code>https://www.googleapis.com/auth/yt-analytics.readonly</code> — to show views,
              watch time, and audience metrics for your published clips in your ViralAI analytics
              dashboard.
            </li>
          </ul>
          <p>
            We request the narrowest set of scopes that makes these features work. If you decline a
            scope, the related feature is simply unavailable.
          </p>

          <h3>4.2 Limited Use</h3>
          <p>
            ViralAI's use and transfer of information received from Google APIs to any other app
            adheres to the{' '}
            <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
              Google API Services User Data Policy
            </a>, including the Limited Use requirements. Specifically:
          </p>
          <ul>
            <li>We use Google user data only to provide and improve the features you use it for.</li>
            <li>We do not transfer or sell Google user data for advertising, and we do not use it for advertising.</li>
            <li>We do not use Google user data to develop, improve, or train generalized or non-personalized AI/ML models.</li>
            <li>
              We do not allow humans to read your Google user data unless (a) you give explicit
              consent for a specific action such as a support request, (b) it is necessary for
              security or to comply with law, or (c) the data is aggregated and anonymized for
              internal operations.
            </li>
          </ul>
          <p>
            You can review or revoke ViralAI's access to your Google Account at any time at{' '}
            <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
              myaccount.google.com/permissions
            </a>.
          </p>
        </section>

        <section>
          <h2>5. How We Use Your Information</h2>
          <ul>
            <li>Create and secure your account and authenticate you.</li>
            <li>Transcribe your videos, detect scene and silence boundaries, and generate short clips, captions, and suggested titles and descriptions.</li>
            <li>Publish and schedule content to the accounts you connect, at your instruction.</li>
            <li>Show analytics and performance insights for content you published through ViralAI.</li>
            <li>Process payments, credits, and refunds, and prevent fraud and abuse.</li>
            <li>Provide support and respond to your requests.</li>
            <li>Monitor, debug, and improve reliability, performance, and security of the Service.</li>
            <li>Send you service and account notifications; send product updates only where permitted, and you can opt out.</li>
            <li>Comply with legal obligations and enforce our Terms.</li>
          </ul>
          <p>
            Where required by law, we rely on the following legal bases: performing our contract
            with you, your consent (for example, connecting an account or optional communications),
            our legitimate interests in operating and securing the Service, and compliance with
            legal obligations.
          </p>
        </section>

        <section>
          <h2>6. AI and Automated Processing</h2>
          <ul>
            <li>
              Transcripts, clip selection, and suggested titles, descriptions, and highlights are
              produced by automated processing, using our own and third-party AI services.
            </li>
            <li>
              We do not send your account information (name, email, phone, address) to these
              services.
            </li>
            <li>
              You review every clip before anything is published — nothing is posted to your
              channels without your action.
            </li>
          </ul>
        </section>

        <section>
          <h2>7. How We Share Your Information</h2>

          <h3>7.1 We do not sell your data</h3>
          <p>
            <strong>We do not sell, rent, or trade your personal information</strong>, and we do not
            share it for cross-context behavioral advertising. This includes all data from connected
            accounts, including Google and YouTube data.
          </p>

          <h3>7.2 When we do share</h3>
          <ul>
            <li>
              <strong>Subprocessors:</strong> vendors that host, process, or transmit data on our
              behalf under contract, listed in Section 8.
            </li>
            <li>
              <strong>Platforms you publish to:</strong> when you publish or schedule a post,
              ViralAI sends that content and its metadata to the platform you chose (YouTube, Meta,
              X, LinkedIn). Their handling is governed by their own policies.
            </li>
            <li>
              <strong>Legal and safety:</strong> when required by law, court order, or valid
              government request, or to protect the rights, safety, or property of ViralAI, our
              users, or the public.
            </li>
            <li>
              <strong>Business transfers:</strong> in connection with a merger, acquisition,
              financing, or sale of assets, with notice to you and continued protection of your
              data.
            </li>
            <li>
              <strong>With your consent:</strong> any other sharing you explicitly authorize.
            </li>
          </ul>
        </section>

        <section>
          <h2>8. Subprocessors</h2>
          <p>We use the following third parties to run the Service:</p>
          <ul>
            <li><strong>Amazon Web Services</strong> (United States) — file storage, processing queue, and compute for video rendering.</li>
            <li><strong>OpenAI</strong> (United States) — fallback speech-to-text transcription and generation of suggested titles, descriptions, and highlights.</li>
            <li><strong>Razorpay</strong> (India) — payment processing and billing.</li>
            <li><strong>Transactional email provider</strong> — delivery of account, security, and notification emails.</li>
            <li><strong>Google reCAPTCHA</strong> (United States) — protects the email sign-in and sign-up forms from bots and abuse. See Section 13.</li>
          </ul>
          <p>
            Each subprocessor is bound by contract to protect your data and use it only to provide
            services to us. We will update this list as our providers change. For a current list at
            any time, contact{' '}
            <a href="mailto:support@vimapi.com">support@vimapi.com</a>.
          </p>
        </section>

        <section>
          <h2>9. Data Retention</h2>
          <table className="legal-table">
            <thead>
              <tr><th>Data</th><th>Retention</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Account information</td>
                <td>While your account is active; deleted within 30 days of account deletion (up to 90 days in backups).</td>
              </tr>
              <tr>
                <td>Uploaded videos, generated clips, transcripts, captions</td>
                <td>While your account is active. You can delete any item at any time. Removed from active storage within 30 days of account deletion and from backups within 90 days.</td>
              </tr>
              <tr>
                <td>OAuth access and refresh tokens for connected accounts</td>
                <td>Until you disconnect the account or delete your ViralAI account. Revoked immediately on disconnect and deleted within 30 days.</td>
              </tr>
              <tr>
                <td>Analytics metrics pulled from connected platforms</td>
                <td>While the account is connected, and up to 12 months afterward for historical reporting.</td>
              </tr>
              <tr>
                <td>Payment and transaction records</td>
                <td>As long as required by applicable tax, accounting, and anti-fraud laws.</td>
              </tr>
              <tr>
                <td>Support communications</td>
                <td>Up to 24 months after the matter is resolved.</td>
              </tr>
              <tr>
                <td>Server and security logs</td>
                <td>Up to 90 days, except where a longer period is needed to investigate an incident.</td>
              </tr>
            </tbody>
          </table>
          <p>
            <strong>Content already published</strong> to YouTube, Meta, X, or LinkedIn stays on
            those platforms after you delete it from ViralAI. You must remove it there directly.
          </p>
        </section>

        <section>
          <h2>10. Managing and Revoking Connected Accounts</h2>
          <ul>
            <li>In ViralAI, open <strong>Social Accounts</strong> and choose <strong>Disconnect</strong> for any platform. This deletes the stored tokens for that account.</li>
            <li>For Google/YouTube, you can also revoke access at{' '}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">myaccount.google.com/permissions</a>.
            </li>
            <li>For Meta, X, and LinkedIn, you can revoke access in each platform's connected-apps or app-permissions settings.</li>
          </ul>
          <p>Revoking access in the platform does not delete your ViralAI account or your uploaded content; use Section 11 for that.</p>
        </section>

        <section>
          <h2>11. Your Rights and Choices</h2>
          <p>Subject to your location and applicable law, you can:</p>
          <ul>
            <li><strong>Access</strong> the personal information we hold about you.</li>
            <li><strong>Correct</strong> inaccurate or incomplete information.</li>
            <li><strong>Delete</strong> your account and associated content.</li>
            <li><strong>Export</strong> a copy of your data in a portable format.</li>
            <li><strong>Object to or restrict</strong> certain processing, and <strong>withdraw consent</strong> where processing is based on consent.</li>
            <li><strong>Opt out</strong> of non-essential communications.</li>
          </ul>
          <p>
            <strong>EU/UK (GDPR):</strong> you also have the right to data portability and to lodge
            a complaint with your local supervisory authority.
          </p>
          <p>
            <strong>California (CCPA/CPRA):</strong> you have the right to know, delete, correct, and
            opt out of "sale" or "sharing" of personal information (we do neither), and the right not
            to be discriminated against for exercising these rights.
          </p>
          <p>
            To exercise any right, email{' '}
            <a href="mailto:support@vimapi.com">support@vimapi.com</a>. We verify requests
            against your account and respond within the timeframe required by law (generally within
            30 days). You may use an authorized agent where the law permits.
          </p>
        </section>

        <section>
          <h2>12. Deleting Your Account and Data</h2>
          <p>
            To delete your account and associated data, email{' '}
            <a href="mailto:support@vimapi.com">support@vimapi.com</a> (or message us on
            WhatsApp at {whatsappLink}) from your account email address with the subject "Delete my
            account." Where a self-service <strong>Delete Account</strong> option is available in your
            account settings, you can use that instead.
          </p>
          <p>
            Deletion removes your account, uploads, generated clips, and connected-account tokens. We
            complete active-system deletion within 30 days and purge backups within 90 days. Some
            records (for example, payment records) may be retained where the law requires it, as
            described in Section 9.
          </p>
        </section>

        <section>
          <h2>13. Cookies</h2>
          <p>
            We use a session cookie to keep you signed in and a small number of cookies to remember
            preferences. We do not use advertising cookies, and we do not currently run third-party
            analytics or tag managers on the Service. If that changes, we will update this section
            and, where required, ask for your consent first.
          </p>
          <p>
            The email sign-in and sign-up pages use Google reCAPTCHA v3. It runs only on those
            pages, and Google may set cookies and collect hardware and software information, such
            as device and application data, and send it to Google to tell people from bots. Its use
            is subject to the Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
          </p>
        </section>

        <section>
          <h2>14. Data Security</h2>
          <ul>
            <li>All traffic is encrypted in transit using TLS.</li>
            <li>OAuth tokens and other sensitive fields are encrypted at rest using AES-256-GCM.</li>
            <li>Access to production systems and user data is restricted to authorized personnel on a need-to-know basis.</li>
            <li>Systems are monitored for suspicious activity, and security practices are reviewed periodically.</li>
          </ul>
          <p>
            No system is perfectly secure. If we become aware of a breach affecting your personal
            information, we will notify you and the relevant authorities as required by law.
          </p>
        </section>

        <section>
          <h2>15. International Data Transfers</h2>
          <p>
            ViralAI is operated from India and uses infrastructure in the United States and other
            countries. Your information may be processed outside your country of residence, in
            places with different data-protection laws. Where required, we use appropriate
            safeguards (such as Standard Contractual Clauses) for these transfers.
          </p>
        </section>

        <section>
          <h2>16. Children's Privacy</h2>
          <p>
            The Service is not intended for anyone under 18, and we do not knowingly collect
            personal information from children. If you believe a child has provided us information,
            contact <a href="mailto:support@vimapi.com">support@vimapi.com</a> and we will
            delete it.
          </p>
        </section>

        <section>
          <h2>17. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy. Material changes take effect when we post the revised
            policy and update the "Last Updated" date; we will also notify registered users by email
            or in-app notice. Continued use of the Service after a change means you accept the
            updated policy.
          </p>
        </section>

        <section>
          <h2>18. Contact Us</h2>
          <p>
            <strong>Email:</strong> <a href="mailto:support@vimapi.com">support@vimapi.com</a> or{' '}
            <a href="mailto:creator@viralai.tech">creator@viralai.tech</a><br />
            <strong>WhatsApp (messages only):</strong> {whatsappLink}<br />
            <strong>Website:</strong> <a href="https://viralai.tech">viralai.tech</a>
          </p>
        </section>

        <footer className="legal-footer">
          <p><strong>Last Updated:</strong> September 9, 2026</p>
        </footer>
      </div>
    </div>
  );
};

export default Privacy;
